import React, { useEffect, useState } from "react";
import "./LeaveList.scss";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "antd";
import {
  cancelPendingLeaveRequestAPI,
  getUserLeaveRequestAPI,
  requestCancelApprovedLeaveAPI,
} from "../../../Redux/Apis/leaveApi";
import { setCurrentPage, setLeaveStatusFilter } from "../../../Redux/Slices/leaveSlice";
import LeaveStatusFilter from "../LeaveStatusFilter/LeaveStatusFilter";
import Loader from "../../atoms/Loader/Loader";
import {
  checkPermission,
  permissionType,
  isWfhLeaveRow,
} from "../../../app/utils/common/Functions";
import { formatLeaveStatusLabel } from "../../../app/utils/common/leaveStatusDisplay";

function leaveCalendarDay(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  const datePart = s.includes("T") ? s.split("T")[0] : s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function leaveStartCalendarDay(leave) {
  return leaveCalendarDay(leave?.startDate);
}

function isSingleDayLeave(leave) {
  const start = leaveCalendarDay(leave?.startDate);
  const end = leaveCalendarDay(leave?.endDate) || start;
  return Boolean(start && end && start === end);
}

function isLeaveStartDateBeforeToday(leave) {
  const start = leaveStartCalendarDay(leave);
  if (!start) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return start < today;
}

const formatLeaveDate = (value) =>
  new Date(value).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const formatWorkLocation = (workLocation) => {
  if (workLocation === "wfh") return "WFH";
  if (workLocation === "wfs") return "WFS";
  if (workLocation === "wfo") return "WFO";
  return "—";
};

const getLeaveStatusStyles = (leave) => {
  const leaveStatus = String(leave.status || "").toLowerCase();
  const cancelReq = String(leave.cancelRequestStatus || "none").toLowerCase();
  const cancelPending = leaveStatus === "approved" && cancelReq === "pending";

  if (leaveStatus === "pending") {
    return { text: "text-yellow-500", dot: "bg-yellow-500" };
  }
  if (leaveStatus === "rejected") {
    return { text: "text-red-500", dot: "bg-red-500" };
  }
  if (leaveStatus === "cancelled") {
    return { text: "text-slate-500", dot: "bg-slate-500" };
  }
  if (cancelPending) {
    return { text: "text-orange-600", dot: "bg-orange-500" };
  }
  return { text: "text-green-500", dot: "bg-green-500" };
};

const LeaveList = ({ onEditLeave }) => {
  const dispatch = useDispatch();
  const canUpdateLeave = checkPermission(permissionType.LEAVE_REQUEST, "update");
  const canDeleteLeave = checkPermission(permissionType.LEAVE_REQUEST, "delete");
  const canCancelPendingLeave = canUpdateLeave || canDeleteLeave;
  const canCancelApprovedLeave = checkPermission(permissionType.LEAVE_REQUEST, "cancel approved");
  const canUpdateWfh = checkPermission(permissionType.WFH_REQUESTS, "update");
  const canDeleteWfh = checkPermission(permissionType.WFH_REQUESTS, "delete");
  const canCancelPendingWfh = canUpdateWfh || canDeleteWfh;
  const canCancelApprovedWfh = checkPermission(permissionType.WFH_REQUESTS, "cancel approved");
  const showLeaveActions =
    canCancelPendingLeave ||
    canUpdateLeave ||
    canCancelApprovedLeave ||
    canCancelPendingWfh ||
    canUpdateWfh ||
    canCancelApprovedWfh;

  const rowActionFlags = (leave) => {
    const wfh = isWfhLeaveRow(leave);
    return {
      canUpdate: wfh ? canUpdateWfh : canUpdateLeave,
      canCancelPending: wfh ? canCancelPendingWfh : canCancelPendingLeave,
      canCancelApproved: wfh ? canCancelApprovedWfh : canCancelApprovedLeave,
    };
  };

  const { userLeaves, pagination, loading, isLeavesUpdated, statusFilter } = useSelector(
    (state) => state.leave
  );
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedLeaveRequestId, setSelectedLeaveRequestId] = useState(null);
  const [cancelMode, setCancelMode] = useState("pending");

  const fetchUserLeaves = () => {
    dispatch(
      getUserLeaveRequestAPI({
        page: pagination.currentPage,
        limit: pagination.pageSize,
        status: statusFilter || undefined,
      })
    );
  };

  useEffect(() => {
    fetchUserLeaves();
  }, [pagination.currentPage, pagination.pageSize, statusFilter, dispatch]);

  useEffect(() => {
    if (isLeavesUpdated) {
      fetchUserLeaves();
    }
  }, [isLeavesUpdated]);

  const handlePageChange = (newPage) => {
    dispatch(setCurrentPage(newPage));
  };

  const openCancelLeaveModal = (leaveRequestId, mode) => {
    setSelectedLeaveRequestId(leaveRequestId);
    setCancelMode(mode);
    setCancelReason("");
    setIsCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
    setCancelReason("");
    setSelectedLeaveRequestId(null);
  };

  const onConfirmCancelLeave = async () => {
    if (!selectedLeaveRequestId) return;
    if (!cancelReason.trim()) {
      window.alert("Cancellation reason is required.");
      return;
    }
    if (cancelMode === "approved" && cancelReason.trim().length < 3) {
      window.alert(
        "Please enter at least 3 characters explaining why you need this leave cancelled."
      );
      return;
    }
    try {
      if (cancelMode === "pending") {
        await dispatch(
          cancelPendingLeaveRequestAPI({
            leaveRequestId: selectedLeaveRequestId,
            comment: cancelReason.trim(),
          })
        ).unwrap();
      } else {
        await dispatch(
          requestCancelApprovedLeaveAPI({
            leaveRequestId: selectedLeaveRequestId,
            comment: cancelReason.trim(),
          })
        ).unwrap();
      }
      closeCancelModal();
    } catch (_err) {
      // Toast handled by reducer
    }
  };

  const renderLeaveStatus = (leave) => {
    const styles = getLeaveStatusStyles(leave);
    return (
      <span className={`leave-request-card__status ${styles.text}`}>
        <span className={`leave-request-card__status-dot ${styles.dot}`} />
        {formatLeaveStatusLabel(leave)}
      </span>
    );
  };

  const renderLeaveActions = (leave, rowActs) => {
    const leaveStatus = String(leave.status || "").toLowerCase();
    const cancelReq = String(leave.cancelRequestStatus || "none").toLowerCase();
    const cancelPending = leaveStatus === "approved" && cancelReq === "pending";
    const leaveStartPassed = isLeaveStartDateBeforeToday(leave);

    if (!["pending", "approved"].includes(leaveStatus)) {
      return <span className="text-xs text-gray-400">—</span>;
    }

    return (
      <div className="leave-actions-cell">
        {rowActs.canUpdate &&
          typeof onEditLeave === "function" &&
          leaveStatus === "pending" ? (
          <button
            type="button"
            className="px-2 py-1 text-xs rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
            onClick={() => onEditLeave(leave)}
          >
            Edit
          </button>
        ) : null}
        {rowActs.canCancelPending && leaveStatus === "pending" ? (
          <button
            type="button"
            className="px-2 py-1 text-xs rounded bg-amber-50 text-amber-900 hover:bg-amber-100 border border-amber-200"
            onClick={() => openCancelLeaveModal(leave.leaveRequestId, "pending")}
          >
            Cancel request
          </button>
        ) : null}
        {rowActs.canCancelApproved &&
          leaveStatus === "approved" &&
          !cancelPending &&
          !leaveStartPassed ? (
          <button
            type="button"
            className="px-2 py-1 text-xs rounded bg-amber-50 text-amber-900 hover:bg-amber-100 border border-amber-200"
            onClick={() => openCancelLeaveModal(leave.leaveRequestId, "approved")}
          >
            Request cancellation
          </button>
        ) : null}
        {rowActs.canCancelApproved &&
          leaveStatus === "approved" &&
          !cancelPending &&
          leaveStartPassed ? (
          <span
            className="text-xs text-gray-500 max-w-[9rem] leading-snug"
            title="Cancellation is only allowed before the leave start date."
          >
            —
          </span>
        ) : null}
        {cancelPending ? (
          <span className="text-xs text-orange-700">Awaiting HR</span>
        ) : null}
      </div>
    );
  };

  const renderPaginationButtons = () => {
    const buttons = [];

    buttons.push(
      <button
        key="prev"
        type="button"
        onClick={() => handlePageChange(pagination.currentPage - 1)}
        disabled={pagination.currentPage === 1}
        className={`px-3 py-1 rounded-md ${pagination.currentPage === 1
          ? "bg-[#e7ecf8] text-[#94a0b8] cursor-not-allowed"
          : "bg-btn-primary text-white "
          }`}
      >
        Previous
      </button>
    );

    for (let i = 1; i <= pagination.totalPages; i++) {
      buttons.push(
        <button
          key={i}
          type="button"
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-md ${pagination.currentPage === i
            ? "bg-btn-primary text-white"
            : "bg-[#eef2fc] text-[#687895]"
            }`}
        >
          {i}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        type="button"
        onClick={() => handlePageChange(pagination.currentPage + 1)}
        disabled={pagination.currentPage >= pagination.totalPages}
        className={`px-3 py-1 rounded-md ${pagination.currentPage === pagination.totalPages
          ? "bg-[#e7ecf8] text-[#94a0b8] cursor-not-allowed"
          : "bg-btn-primary text-white "
          }`}
      >
        Next
      </button>
    );

    return buttons;
  };

  const getRowNumber = (index) =>
    (pagination.currentPage - 1) * pagination.pageSize + index + 1;

  const renderLeaveCard = (leave, index) => {
    const rowActs = rowActionFlags(leave);

    return (
      <article
        key={leave.leaveRequestId}
        className="leave-request-card leave-management-card"
      >
        <header className="leave-request-card__header leave-management-card__header">
          <span className="leave-request-card__index">{getRowNumber(index)}</span>
          <span className="leave-request-card__category leave-management-category-badge">
            {leave.leaveType}
          </span>
          {renderLeaveStatus(leave)}
        </header>

        <dl className="leave-request-card__details leave-management-card__details">
          <div className="leave-request-card__detail leave-request-card__detail--full leave-management-detail--highlight">
            <dt>Period</dt>
            {isSingleDayLeave(leave) ? (
              <dd className="leave-management-period-boxes leave-management-period-boxes--single">
                <div className="leave-management-date-box">
                  <span className="leave-management-date-box__label">Date</span>
                  <span className="leave-management-date-box__value">
                    {formatLeaveDate(leave.startDate)}
                  </span>
                  {leave.startTime || leave.endTime ? (
                    <span className="leave-management-date-box__time">
                      {[leave.startTime, leave.endTime].filter(Boolean).join(" – ")}
                    </span>
                  ) : null}
                </div>
              </dd>
            ) : (
              <dd className="leave-management-period-boxes">
                <div className="leave-management-date-box">
                  <span className="leave-management-date-box__label">From</span>
                  <span className="leave-management-date-box__value">
                    {formatLeaveDate(leave.startDate)}
                  </span>
                  {leave.startTime ? (
                    <span className="leave-management-date-box__time">{leave.startTime}</span>
                  ) : null}
                </div>
                <div className="leave-management-date-box">
                  <span className="leave-management-date-box__label">To</span>
                  <span className="leave-management-date-box__value">
                    {formatLeaveDate(leave.endDate)}
                  </span>
                  {leave.endTime ? (
                    <span className="leave-management-date-box__time">{leave.endTime}</span>
                  ) : null}
                </div>
              </dd>
            )}
          </div>
          <div className="leave-request-card__detail">
            <dt>Leave category</dt>
            <dd>{leave.leaveCategory}</dd>
          </div>
          <div className="leave-request-card__detail leave-request-card__detail--full leave-management-detail--highlight">
            <dt>Reason</dt>
            <dd>{leave.reason || "—"}</dd>
          </div>
        </dl>

        {showLeaveActions ? (
          <footer className="leave-request-card__actions leave-management-card__actions">
            {renderLeaveActions(leave, rowActs)}
          </footer>
        ) : null}
      </article>
    );
  };

  return (
    <>
      <div className="leave-list-container leave-management-list-panel card-border card-body mb-4">
        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="leave-management-list-panel__head">
              <h2 className="leave-management-list-panel__title">Your leave requests</h2>
              <span className="leave-management-list-panel__count">
                {pagination.totalData} total
              </span>
            </div>

            <LeaveStatusFilter
              value={statusFilter}
              onChange={(next) => dispatch(setLeaveStatusFilter(next))}
              className="leave-list__status-filter"
            />

            {userLeaves.length === 0 ? (
              <div className="leave-management-empty">
                <p className="leave-management-empty__title">No leave requests yet</p>
                <p className="leave-management-empty__text">
                  Apply for leave using the buttons above. Your requests will appear here.
                </p>
              </div>
            ) : (
              <div className="leave-list-cards leave-management-cards" aria-label="Leave requests">
                {userLeaves.map((leave, index) => renderLeaveCard(leave, index))}
              </div>
            )}

            {userLeaves.length > 0 ? (
              <div className="leave-list-pagination leave-management-pagination">
                <div className="leave-management-pagination__meta">
                  Showing {userLeaves.length} of {pagination.totalData} entries
                </div>
                {pagination.totalPages > 1 ? (
                  <div className="leave-management-pagination__buttons">
                    {renderPaginationButtons()}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>

      <Modal
        title={
          cancelMode === "pending"
            ? "Cancel Pending Leave Request"
            : "Request cancellation of approved leave"
        }
        open={isCancelModalOpen}
        wrapClassName="leave-request-modal-wrap"
        onCancel={closeCancelModal}
        onOk={onConfirmCancelLeave}
        okText={cancelMode === "pending" ? "Confirm Cancel" : "Confirm Cancel"}
        cancelText="Close"
        okButtonProps={{ danger: true }}
      >
        <p className="mb-2 text-sm text-gray-600">
          {cancelMode === "pending"
            ? "Add a cancellation reason (required)."
            : "Explain why you need this approved leave cancelled (required, at least 3 characters). HR/Admin will be notified and must approve before the leave is removed."}
        </p>
        <textarea
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          rows={4}
          placeholder={cancelMode === "pending" ? "Reason for cancellation" : "Reason (optional)"}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
        />
      </Modal>
    </>
  );
};

export default LeaveList;
